import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DataPreviewPanel = ({ 
  previewData = [], 
  mappings = {}, 
  sourceColumns = [] 
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [showRawData, setShowRawData] = useState(false);
  
  const rowsPerPage = 5;
  const totalPages = Math.ceil(previewData?.length / rowsPerPage);
  const currentRows = previewData?.slice(
    currentPage * rowsPerPage, 
    (currentPage + 1) * rowsPerPage
  );

  const getMappedColumns = () => {
    return Object.entries(mappings)?.filter(([_, mapping]) => mapping?.field && mapping?.field !== 'ignore')?.map(([columnName, mapping]) => ({
        sourceColumn: columnName,
        targetField: mapping?.field,
        label: getFieldLabel(mapping?.field)
      }));
  };

  const getFieldLabel = (field) => {
    const fieldLabels = {
      'product_name': 'Product Name',
      'brand': 'Brand',
      'mpn': 'MPN',
      'ean': 'EAN/UPC',
      'sku': 'SKU',
      'price': 'Price',
      'stock': 'Stock',
      'category': 'Category',
      'description': 'Description',
      'supplier': 'Supplier'
    };
    return fieldLabels?.[field] || field;
  };

  const formatCellValue = (value, field) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted-foreground italic">—</span>;
    }

    if (field === 'price') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        return <span className="font-mono text-success">${numValue?.toFixed(2)}</span>;
      }
    }

    if (field === 'stock') {
      const numValue = parseInt(value);
      if (!isNaN(numValue)) {
        return (
          <span className={`font-mono ${numValue > 0 ? 'text-success' : 'text-error'}`}>
            {numValue?.toLocaleString()}
          </span>
        );
      }
    }

    return <span className="truncate" title={String(value)}>{String(value)}</span>;
  };

  const getValidationStatus = () => {
    const mappedColumns = getMappedColumns();
    const hasProductName = mappedColumns?.some(col => col?.targetField === 'product_name');
    const hasPrice = mappedColumns?.some(col => col?.targetField === 'price');
    
    return {
      isValid: hasProductName && hasPrice,
      missingFields: [
        ...(!hasProductName ? ['Product Name'] : []),
        ...(!hasPrice ? ['Price'] : [])
      ]
    };
  };

  const validationStatus = getValidationStatus();
  const mappedColumns = getMappedColumns();

  if (mappedColumns?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Data Preview</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Preview how your data will be processed
          </p>
        </div>
        <div className="p-8 text-center">
          <Icon name="Table" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">No Columns Mapped</h4>
          <p className="text-muted-foreground">
            Map columns in the left panel to see a preview of your processed data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Data Preview</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Showing {mappedColumns?.length} mapped field{mappedColumns?.length !== 1 ? 's' : ''} • {previewData?.length} rows
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowRawData(!showRawData)}
              iconName={showRawData ? 'Eye' : 'EyeOff'}
              iconPosition="left"
            >
              {showRawData ? 'Processed' : 'Raw Data'}
            </Button>
          </div>
        </div>

        {/* Validation Status */}
        <div className="mt-3">
          {validationStatus?.isValid ? (
            <div className="flex items-center space-x-2 text-success">
              <Icon name="CheckCircle" size={16} />
              <span className="text-sm font-medium">Ready for processing</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2 text-error">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm font-medium">
                Missing required fields: {validationStatus?.missingFields?.join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>
      {/* Data Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted bg-opacity-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-12">
                #
              </th>
              {mappedColumns?.map((column, index) => (
                <th 
                  key={`${column?.sourceColumn}-${index}`}
                  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                >
                  <div className="flex flex-col">
                    <span className="font-semibold text-foreground">{column?.label}</span>
                    <span className="text-xs normal-case opacity-75">
                      {showRawData ? column?.sourceColumn : `from ${column?.sourceColumn}`}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {currentRows?.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-muted hover:bg-opacity-30 transition-colors">
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {currentPage * rowsPerPage + rowIndex + 1}
                </td>
                {mappedColumns?.map((column, colIndex) => (
                  <td 
                    key={`${column?.sourceColumn}-${colIndex}`}
                    className="px-4 py-3 text-sm text-foreground max-w-xs"
                  >
                    {formatCellValue(row?.[column?.sourceColumn], column?.targetField)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {currentPage * rowsPerPage + 1} to {Math.min((currentPage + 1) * rowsPerPage, previewData?.length)} of {previewData?.length} rows
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage === totalPages - 1}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Footer Summary */}
      <div className="p-4 border-t border-border bg-muted bg-opacity-30">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <div>
            <span className="font-medium text-muted-foreground">Mapped Fields:</span>
            <p className="text-foreground mt-1">{mappedColumns?.length} of {sourceColumns?.length} columns</p>
          </div>
          <div>
            <span className="font-medium text-muted-foreground">Data Rows:</span>
            <p className="text-foreground mt-1">{previewData?.length?.toLocaleString()} rows ready</p>
          </div>
          <div>
            <span className="font-medium text-muted-foreground">Status:</span>
            <p className={`mt-1 font-medium ${validationStatus?.isValid ? 'text-success' : 'text-error'}`}>
              {validationStatus?.isValid ? 'Ready to process' : 'Needs attention'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataPreviewPanel;